import React, { useState, useEffect } from 'react';
import { CAPTION_PRESETS } from '../lib/CaptionPresets';
import './CaptionPhonePreview.css';

const SAMPLE_SENTENCES = [
    { text: "Here's what you need to know", highlight: "need to know" },
    { text: "This is really important", highlight: "really important" },
    { text: "Watch until the end", highlight: "the end" }
];

export const CaptionPhonePreview = ({ styleId, size, verticalPosition }) => {
    const [sentenceIndex, setSentenceIndex] = useState(0);
    const [wordIndex, setWordIndex] = useState(0);

    const preset = CAPTION_PRESETS.find(p => p.id === styleId) || CAPTION_PRESETS[0];
    const currentSentence = SAMPLE_SENTENCES[sentenceIndex];
    const words = currentSentence.text.split(' ');

    // Cycle through sentences
    useEffect(() => {
        const interval = setInterval(() => {
            setSentenceIndex(prev => (prev + 1) % SAMPLE_SENTENCES.length);
            setWordIndex(0);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    // Animate word highlighting - current word gets emphasized
    useEffect(() => {
        const interval = setInterval(() => {
            setWordIndex(prev => (prev + 1) % (words.length + 1));
        }, 400);
        return () => clearInterval(interval);
    }, [words.length, sentenceIndex]);

    // Calculate font size based on slider (40-150 maps to 10-24px in preview)
    const previewFontSize = Math.round(10 + ((size - 40) / 110) * 14);

    // Highlight color based on preset
    const highlightColor = preset.preview.highlight || preset.preview.color || '#00d2d3';

    // Style configuration
    const baseStyle = {
        fontFamily: preset.preview.fontFamily || 'Arial, sans-serif',
        fontWeight: preset.preview.fontWeight || 'bold',
        fontSize: `${previewFontSize}px`,
        textTransform: preset.preview.textTransform || 'uppercase',
        textShadow: preset.preview.textShadow || '2px 2px 4px rgba(0,0,0,0.8)',
        lineHeight: 1.3,
        transition: 'all 0.15s ease-out',
    };

    // Render words with per-word color change and grow/shrink effect
    // Current word: highlighted color + slight grow
    // Previous words: white + slight shrink
    // Future words: dimmed
    const renderCaptionText = () => {
        return words.map((word, idx) => {
            const isCurrent = idx === wordIndex;
            const isSpoken = idx < wordIndex;
            const isFuture = idx > wordIndex;

            // Deep Diver: white box, black text style
            if (preset.id === 'deep_diver') {
                return (
                    <span
                        key={idx}
                        style={{
                            ...baseStyle,
                            opacity: isFuture ? 0.3 : 1,
                            background: '#fff',
                            color: '#000',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            marginRight: '4px',
                            display: 'inline-block',
                            transform: isCurrent ? 'scale(1.1)' : isSpoken ? 'scale(0.95)' : 'scale(1)',
                        }}
                    >
                        {word}
                    </span>
                );
            }

            // Karaoke: boxed style
            if (preset.id === 'karaoke') {
                return (
                    <span
                        key={idx}
                        style={{
                            ...baseStyle,
                            color: isCurrent ? highlightColor : isSpoken ? '#fff' : preset.preview.color,
                            marginRight: '4px',
                            border: preset.preview.border,
                            borderRadius: preset.preview.borderRadius,
                            padding: '2px 4px',
                            display: 'inline-block',
                            transform: isCurrent ? 'scale(1.1)' : isSpoken ? 'scale(0.95)' : 'scale(1)',
                        }}
                    >
                        {word}
                    </span>
                );
            }

            // Default, beasty, pod_p, youshaei, mozi styles
            // Current word: highlight color + grow
            // Spoken words: white + slight shrink
            // Future words: slightly dimmed base color
            return (
                <span
                    key={idx}
                    style={{
                        ...baseStyle,
                        color: isCurrent ? highlightColor : isSpoken ? '#fff' : (preset.preview.color || 'rgba(255,255,255,0.6)'),
                        marginRight: '4px',
                        display: 'inline-block',
                        transform: isCurrent ? 'scale(1.15)' : isSpoken ? 'scale(0.9)' : 'scale(1)',
                        opacity: isFuture ? 0.5 : 1,
                    }}
                >
                    {word}
                </span>
            );
        });
    };

    return (
        <div className="caption-phone-preview">
            <div className="phone-frame">
                <div className="phone-notch" />
                <div className="phone-screen">
                    <div
                        className="caption-container"
                        style={{ bottom: `${verticalPosition}%` }}
                    >
                        <div className="caption-text">
                            {renderCaptionText()}
                        </div>
                    </div>
                </div>
            </div>
            <div className="preview-label">Live Preview</div>
        </div>
    );
};
